import { defineCollection } from "@rebasepro/cms-types";
import { day, moment } from "../words";
import playersCollection from "./players";
import type { CollectionConfig } from "@rebasepro/types";

// The Daily Gig board (songbuzz_solo_daily): one score per day and device, the name shown on the board and the score
// after every song. backend/functions/songbuzz.ts creates the table and writes it; admins can rename or remove a score.
// `day` is a date in the database, text here: a collection's key can't be a date.
const dailyScoresCollection = defineCollection({
    name: "Daily Gig scores",
    singularName: "Daily Gig score",
    slug: "daily_scores",
    table: "songbuzz_solo_daily",
    description: "The Daily Gig leaderboard: one score per player and day.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operations: ["update", "delete"], roles: ["admin"] },
    ],
    properties: {
        day: { name: "Day", type: "string", isId: "manual", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "day" } } },
        // the account ("u:<uid>") or, for Solo without one, the device
        sid: { name: "Player id", type: "string", isId: "manual", admin: { readOnly: true, hideFromCollection: true } },
        player: {
            name: "Player", type: "relation",
            relation: { kind: "via", cardinality: "one", target: (): CollectionConfig => playersCollection, relationName: "player", joinPath: [{ table: "songbuzz_players", on: { from: "sid", to: "id" } }] },
            admin: { readOnly: true },
        },
        name: { name: "Name on the board", type: "string" },
        hype: { name: "Score (hype)", type: "number", columnType: "bigint", validation: { required: true }, admin: { readOnly: true } },
        songs: { name: "Songs played", type: "number", columnType: "integer", admin: { readOnly: true } },
        rights: { name: "Songs right", type: "number", columnType: "integer", admin: { readOnly: true } },
        won: { name: "Beat the day's target", type: "boolean", admin: { readOnly: true } },
        ranked: { name: "On the board", description: "Off: the score is kept but nobody sees it on the leaderboard", type: "boolean", defaultValue: false, validation: { required: true } },
        // a short hash of the look they wore, so the board draws them as they were
        look: { name: "Look code", type: "string", admin: { readOnly: true, hideFromCollection: true } },
        cum: {
            name: "Hype after each song", type: "array", columnType: "jsonb", of: { name: "Hype", type: "number" },
            admin: { readOnly: true, hideFromCollection: true, span: 4, Preview: "../../admin/src/previews/HypeCurve" },
        },
        at: { name: "When", type: "date", autoValue: "on_create", validation: { required: true }, admin: { readOnly: true } },
    },
    admin: {
        icon: "CalendarDays",
        group: "Daily Gig",
        openEntityMode: "side_panel",
        sort: ["at", "desc"],
        display: {
            title: ({ entity }) => entity.values.name || "No name",
            // the day it was for, how it went and whether the board shows it: the list's columns leave the status out
            subtitle: ({ entity }) => [`Daily Gig ${day(entity.values.day)}`, entity.values.won ? "beat the target" : "missed the target", entity.values.ranked ? "" : "off the board", `played ${moment(entity.values.at)}`].filter(Boolean).join(" · "),
            status: ({ entity }) => entity.values.ranked ? undefined : "Off the board",
            date: "at",
        },
        listProperties: ["hype", "rights", "songs"],
        form: {
            sections: [
                { key: "score", properties: ["player", "name", "day", "hype", "rights", "songs", "won", "ranked", "cum", "at"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["sid", "look"] },
            ],
        },
    },
});

export default dailyScoresCollection;
